<template>
  <header class="header" :class="{ 'header-scrolled': isScrolled }">
    <div class="header-shell">
      <router-link to="/" class="brand">
        <span class="brand-mark" aria-hidden="true">
          <i class="fas fa-music"></i>
        </span>
        <span class="brand-copy">
          <span class="brand-name">{{ tr('header.brandName') }}</span>
          <span class="brand-location">{{ tr('header.brandLocation') }}</span>
        </span>
      </router-link>

      <div class="header-actions">
        <nav class="nav-desktop" :aria-label="tr('header.mainNavLabel')">
          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.path">
              <router-link :to="item.path" class="nav-link" active-class="nav-link-active">
                {{ $t(item.label) }}
              </router-link>
            </li>
          </ul>
        </nav>

        <router-link to="/contact" class="booking-pill">
          <i class="fas fa-calendar-check"></i>
          <span>{{ tr('header.booking') }}</span>
        </router-link>

        <div class="language-selector">
          <button
            @click="toggleLanguageMenu"
            class="language-button"
            :aria-label="tr('nav.language')"
            aria-haspopup="true"
            :aria-expanded="isLanguageMenuOpen"
          >
            <span
              class="language-flag language-flag-current"
              :class="selectedLanguage.flag ? 'language-flag--emoji' : 'language-flag--galicia'"
              aria-hidden="true"
            >
              <span v-if="selectedLanguage.flag" class="language-emoji">{{ selectedLanguage.flag }}</span>
            </span>
            <span class="language-current">{{ currentLocale.toUpperCase() }}</span>
            <i class="fas fa-chevron-down language-icon" :class="{ rotate: isLanguageMenuOpen }"></i>
          </button>

          <Transition name="dropdown">
            <div v-if="isLanguageMenuOpen" class="language-dropdown">
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="selectLanguage(lang.code)"
                class="language-option"
                :class="{ active: currentLocale === lang.code }"
              >
                <span
                  class="language-flag"
                  :class="lang.flag ? 'language-flag--emoji' : 'language-flag--galicia'"
                  aria-hidden="true"
                >
                  <span v-if="lang.flag" class="language-emoji">{{ lang.flag }}</span>
                </span>
                <span class="language-name">{{ lang.name }}</span>
                <i v-if="currentLocale === lang.code" class="fas fa-check language-check"></i>
              </button>
            </div>
          </Transition>
        </div>

        <router-link
          v-if="isAuthenticated"
          to="/admin"
          class="admin-button"
          active-class="admin-button-active"
        >
          <i class="fas fa-screwdriver-wrench"></i>
          <span class="admin-text">{{ tr('header.admin') }}</span>
        </router-link>
        <router-link v-else to="/login" class="login-button">
          <i class="fas fa-user-lock"></i>
          <span class="login-text">{{ tr('header.login') }}</span>
        </router-link>

        <button
          @click="toggleMobileMenu"
          class="mobile-menu-button"
          :aria-label="isMobileMenuOpen ? tr('header.closeMenu') : tr('header.openMenu')"
          aria-haspopup="true"
          :aria-expanded="isMobileMenuOpen"
        >
          <span class="hamburger" :class="{ active: isMobileMenuOpen }">
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </span>
        </button>
      </div>
    </div>

    <Transition name="mobile-menu">
      <nav v-if="isMobileMenuOpen" class="nav-mobile" :aria-label="tr('header.mobileNavLabel')">
        <div class="nav-mobile-inner">
          <router-link to="/contact" class="mobile-cta" @click="closeMobileMenu">
            <i class="fas fa-calendar-check"></i>
            {{ tr('header.booking') }}
          </router-link>
          <ul class="nav-mobile-list">
            <li v-for="item in navItems" :key="item.path">
              <router-link
                :to="item.path"
                class="nav-mobile-link"
                active-class="nav-mobile-link-active"
                @click="closeMobileMenu"
              >
                <i :class="item.icon" class="nav-mobile-icon"></i>
                <span>{{ $t(item.label) }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </nav>
    </Transition>
  </header>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const { isAuthenticated } = useAuth();

const isMobileMenuOpen = ref(false);
const isLanguageMenuOpen = ref(false);
const isScrolled = ref(false);

const navItems = [
  { path: '/', label: 'nav.home', icon: 'fas fa-house' },
  { path: '/about', label: 'nav.about', icon: 'fas fa-circle-info' },
  { path: '/school', label: 'nav.school', icon: 'fas fa-graduation-cap' },
  { path: '/events', label: 'nav.events', icon: 'fas fa-calendar-days' },
  { path: '/news', label: 'nav.news', icon: 'fas fa-newspaper' },
  { path: '/contact', label: 'nav.contact', icon: 'fas fa-envelope' },
];

const languages = [
  { code: 'gl', name: 'Galego', flag: '' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const $locale = instance?.appContext.config.globalProperties.$locale;
const $setLocale = instance?.appContext.config.globalProperties.$setLocale;
const tr = (key: string) => ($t ? $t(key) : key);

const currentLocale = computed({
  get: () => $locale?.locale || 'gl',
  set: (val: string) => {
    if ($setLocale) {
      $setLocale(val);
      localStorage.setItem('preferred-language', val);
    }
  },
});

const selectedLanguage = computed(
  () => languages.find((language) => language.code === currentLocale.value) ?? languages[0],
);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  isLanguageMenuOpen.value = false;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const toggleLanguageMenu = () => {
  isLanguageMenuOpen.value = !isLanguageMenuOpen.value;
};

const selectLanguage = (lang: string) => {
  currentLocale.value = lang;
  isLanguageMenuOpen.value = false;
  document.documentElement.lang = lang;
};

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (!target.closest('.language-selector')) {
    isLanguageMenuOpen.value = false;
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

const handleResize = () => {
  if (window.innerWidth >= 768) {
    isMobileMenuOpen.value = false;
  }
};

watch(
  () => route.fullPath,
  () => {
    isMobileMenuOpen.value = false;
  },
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize);
  handleScroll();

  const savedLang = localStorage.getItem('preferred-language');
  if (savedLang && languages.some((l) => l.code === savedLang)) {
    currentLocale.value = savedLang;
    document.documentElement.lang = savedLang;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: var(--z-index-sticky);
  background: rgba(10, 24, 36, 0.78);
  backdrop-filter: blur(12px) saturate(125%);
  border-bottom: 1px solid rgba(219, 174, 80, 0.2);
  transition: background-color var(--duration-normal) var(--ease-smooth),
              box-shadow var(--duration-normal) var(--ease-smooth),
              border-color var(--duration-normal) var(--ease-smooth);
}

.header-scrolled {
  background: rgba(9, 20, 30, 0.9);
  box-shadow: 0 10px 28px -16px rgba(0, 0, 0, 0.65);
  border-bottom-color: rgba(219, 174, 80, 0.28);
}

.header-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.9rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
}

@media (min-width: 1024px) {
  .header-shell {
    padding: 0.9rem 1.8rem;
  }
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  color: var(--color-secondary-300);
  font-size: 1.05rem;
  background: linear-gradient(135deg, rgba(219, 174, 80, 0.17), rgba(197, 141, 48, 0.3));
  border: 1px solid rgba(219, 174, 80, 0.34);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.brand-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}

.brand-name {
  font-family: var(--font-family-display);
  font-size: 1.34rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  color: #f8f5ef;
}

.brand-location {
  font-size: 0.74rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(248, 245, 239, 0.8);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.nav-desktop {
  display: none;
}

@media (min-width: 1024px) {
  .nav-desktop {
    display: block;
  }
}

.nav-list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  gap: 0.12rem;
}

.nav-link {
  color: rgba(248, 245, 239, 0.82);
  font-size: 0.89rem;
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  padding: 0.55rem 0.78rem;
  border-radius: 999px;
  transition: var(--transition-all);
}

.nav-link:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
}

.nav-link-active {
  background: rgba(219, 174, 80, 0.26);
  color: #fffefb;
}

.booking-pill {
  display: none;
}

@media (min-width: 1140px) {
  .booking-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    color: var(--color-bg-dark);
    background: var(--color-secondary-300);
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 0.5rem 0.78rem;
    font-size: 0.82rem;
    font-weight: var(--font-weight-semibold);
    text-decoration: none;
    transition: var(--transition-all);
  }

  .booking-pill:hover {
    transform: translateY(-1px);
    background: var(--color-secondary-200);
    box-shadow: var(--shadow-secondary);
  }
}

.language-selector {
  position: relative;
}

.language-button {
  display: inline-flex;
  align-items: center;
  gap: 0.48rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #f8f5ef;
  border-radius: 999px;
  padding: 0.47rem 0.68rem;
  font-size: 0.78rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: var(--transition-all);
}

.language-button:hover {
  background: rgba(255, 255, 255, 0.14);
}

.language-icon {
  font-size: 0.65rem;
  transition: transform var(--duration-fast) var(--ease-out);
}

.language-current {
  min-width: 2ch;
  line-height: 1;
}

.language-icon.rotate {
  transform: rotate(180deg);
}

.language-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 170px;
  padding: 0.3rem;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(16, 41, 59, 0.16);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
}

.language-option {
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--color-text-secondary);
  padding: 0.55rem 0.58rem;
  border-radius: 0.58rem;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.84rem;
  cursor: pointer;
  transition: var(--transition-all);
}

.language-option:hover {
  background: rgba(26, 79, 113, 0.09);
  color: var(--color-primary-900);
}

.language-option.active {
  background: rgba(26, 79, 113, 0.15);
  color: var(--color-primary-900);
  font-weight: var(--font-weight-semibold);
}

.language-flag {
  width: 1.25rem;
  height: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.2rem;
  border: 1px solid rgba(16, 41, 59, 0.14);
  background: rgba(255, 255, 255, 0.7);
  overflow: hidden;
  flex-shrink: 0;
}

.language-flag-current {
  border-color: rgba(255, 255, 255, 0.36);
}

.language-flag--emoji {
  background: transparent;
  border: 0;
  font-size: 0.95rem;
}

.language-emoji {
  line-height: 1;
}

.language-flag--galicia {
  position: relative;
  background: #ffffff;
}

.language-flag--galicia::before {
  content: '';
  position: absolute;
  width: 170%;
  height: 38%;
  background: #4c8fd4;
  transform: rotate(-34deg);
}

.language-check {
  margin-left: auto;
  color: var(--color-primary-700);
}

.admin-button,
.login-button {
  color: #f9f6f0;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  border-radius: 999px;
  padding: 0.47rem 0.68rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  transition: var(--transition-all);
}

.admin-button:hover,
.admin-button-active {
  background: rgba(255, 255, 255, 0.16);
}

.login-button {
  background: rgba(219, 174, 80, 0.22);
  border-color: rgba(219, 174, 80, 0.46);
}

.login-button:hover {
  background: rgba(219, 174, 80, 0.32);
  transform: translateY(-1px);
}

.mobile-menu-button {
  display: inline-flex;
  width: 2.2rem;
  height: 2.2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.07);
  color: #f9f6f0;
  cursor: pointer;
}

@media (min-width: 768px) {
  .mobile-menu-button {
    display: none;
  }
}

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 17px;
}

.hamburger-line {
  width: 100%;
  height: 2px;
  border-radius: 999px;
  background: currentColor;
  transition: transform var(--duration-fast) var(--ease-out),
              opacity var(--duration-fast) var(--ease-out);
}

.hamburger.active .hamburger-line:nth-child(1) {
  transform: translateY(6px) rotate(45deg);
}

.hamburger.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger.active .hamburger-line:nth-child(3) {
  transform: translateY(-6px) rotate(-45deg);
}

.nav-mobile {
  border-top: 1px solid rgba(219, 174, 80, 0.2);
  background: linear-gradient(180deg, rgba(10, 24, 36, 0.97) 0%, rgba(10, 24, 36, 0.92) 100%);
}

.nav-mobile-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.8rem 1rem 1rem;
}

.mobile-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  color: var(--color-bg-dark);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-lg);
  background: var(--color-secondary-300);
  padding: 0.72rem 1rem;
  margin-bottom: 0.72rem;
}

.nav-mobile-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.34rem;
}

.nav-mobile-link {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.76rem 0.88rem;
  text-decoration: none;
  border-radius: var(--border-radius-md);
  color: rgba(249, 246, 240, 0.86);
  transition: var(--transition-all);
}

.nav-mobile-link:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.09);
}

.nav-mobile-link-active {
  color: #ffffff;
  background: rgba(219, 174, 80, 0.17);
}

.nav-mobile-icon {
  width: 1.2rem;
  text-align: center;
  color: var(--color-secondary-300);
}

@media (max-width: 767px) {
  .brand-copy {
    display: none;
  }

  .header-shell {
    padding-inline: 0.9rem;
  }

  .admin-text,
  .login-text {
    display: none;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity var(--duration-fast) var(--ease-out),
              transform var(--duration-fast) var(--ease-out);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity var(--duration-fast) var(--ease-out),
              transform var(--duration-fast) var(--ease-out);
  transform-origin: top;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
